import React, { useState, useEffect } from "react";
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { makeDateString } from '../../../util.js';
import QUERY_NET_SALES_BY_DATE from "../../../graphql/queries/QueryNetSalesByDate.js";

import {
  Pane,
  Paragraph,
  EmptyState,
  InfoSignIcon
} from 'evergreen-ui';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

import {
  DateFieldInput,
} from "../../../inputs/index.js";

const NetSalesByDate = props => {
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const currentpublisher = useSelector((state) => state.currentpublisher.value);

  const getInitialDateRange = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 730);
    startDate.setHours(0, 0, 0, 0);

    return {
      start: startDate.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  };

  const dateRange = getInitialDateRange();
  const [startDate, setStartDate] = useState(dateRange.start);
  const [endDate, setEndDate] = useState(dateRange.end);
  const [chartData, setChartData] = useState([]);

  const handleChangeDate = obj => {
    let date = new Date(obj.val);
    if (obj.key === "startDate") {
      date.setHours(0, 0, 0, 0);
      setStartDate(date.toISOString().split('T')[0]);
    } else {
      date.setHours(23, 59, 59, 999);
      setEndDate(date.toISOString().split('T')[0]);
    }
  }

  const { loading, error, data, refetch } = useQuery(QUERY_NET_SALES_BY_DATE, {
    variables: {
      publisher: String(currentpublisher.id),
      startDate: dateRange.start,
      endDate: dateRange.end
    },
  });

  // this could probably stand to be refactored, along with
  // all the other report functions...
  useEffect(() => {
    if (data) {
      // sum the expenses
      const summedExpenses = {};
      data.expensesbydate.forEach(item => {
        let myKey = "expenseDate";
        if (summedExpenses.hasOwnProperty(item[myKey])) {
          summedExpenses[item[myKey]] = summedExpenses[item[myKey]] + parseFloat(item.amount);
        } else {
          summedExpenses[item[myKey]] = parseFloat(item.amount);
        }
      });
      // sum the sales
      const summedObj = {};
      data.grosssalesbydate.forEach(item => {
        let myKey = publisherGlobalData.accountingMethod === "paid" ? "paidDate" : "shipDate";
        if (summedObj.hasOwnProperty(item[myKey])) {
          summedObj[item[myKey]] = summedObj[item[myKey]] + parseFloat(item.totalAmount);
        } else {
          summedObj[item[myKey]] = parseFloat(item.totalAmount);
        }
        // delete the expenses from the sales
        if (summedExpenses.hasOwnProperty(item[myKey])) {
          summedObj[item[myKey]] = summedObj[item[myKey]] - parseFloat(summedExpenses[item[myKey]]);
        }
      });
      // add any expenses that weren't covered previously
      for (let item in summedExpenses) {
        if (!summedObj.hasOwnProperty(item)) {
          summedObj[item] = parseFloat(summedExpenses[item]) * -1;
        }
      }
      // turn the object into an array
      const finalArr = [];
      for (let item in summedObj) {
        finalArr.push({ key: item, amount: summedObj[item] });
      }
      // finally sort the array by date
      finalArr.sort((a, b) => new Date(a.key) - new Date(b.key));
      setChartData(finalArr);
    }
  }, [data, publisherGlobalData]);

  // Add event listener for dashboard data refresh
  useEffect(() => {
    const handleRefreshDashboardData = () => {
      refetch();
    };

    // Add event listener
    window.addEventListener('refreshDashboardData', handleRefreshDashboardData);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('refreshDashboardData', handleRefreshDashboardData);
    };
  }, [refetch]);

  if (loading) return null;
  if (error) return `Error! ${error}`;

  return (
    <Pane background="white" elevation={2} padding={16} border={"default"} marginRight={16}>
      <Paragraph size={500} fontWeight="bolder" marginRight={8} color="var(--maroon)">Net Sales</Paragraph>
      <Pane display="flex" flexDirection="row" justifyContent="flex-start" marginY={8}>
        <Pane marginRight={32}>
          <DateFieldInput el="startDate" value={startDate} editing={true} handleChange={handleChangeDate} formLabel="Start Date" width={300} />

        </Pane>
        <DateFieldInput el="endDate" value={endDate} editing={true} handleChange={handleChangeDate} formLabel="End Date" width={300} />
      </Pane>

      {data && data.grosssalesbydate.length > 0 ?
        <Pane>

          <LineChart width={350} height={250} data={chartData}
            margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="key" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="amount" stroke="#c09e85" strokeWidth={2} />
          </LineChart>

        </Pane> :
        <Pane height={250} width={350}>
          <EmptyState
            background="light"
            title="No Sales Recorded for Selected Dates"
            orientation="vertical"
            icon={<InfoSignIcon size={12} color="#C1C4D6" />}
            iconBgColor="#EDEFF5"
          />
        </Pane>

      }


    </Pane>
  );
}

export default NetSalesByDate;
